// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/Entertainment.tsx
// 🔧 PURPOSE: "Intrattenimento · Un momento di relax", in tutte le viste:
//             i giochi in fila, a scorrimento orizzontale (ogni card un po'
//             più stretta dello schermo: si vede che ce n'è un'altra). Ogni
//             card ha i colori del suo gioco (GameCards):
//             - Memory: per tutti, apre la pagina del gioco;
//             - "Polpo Goloso" (il labirinto), "Spara Sapori" (le bolle) e
//               "Dal tarallo al trullo" (il 2048):
//               solo dopo l'accesso; senza, il lucchetto e "Accedi per
//               giocare" aprono il login di sempre e, se riesce, portano al
//               gioco; con l'accesso, il record;
//             - in fondo, tratteggiata, "Presto altri giochi".
//             Un gioco nuovo = una card in più nella fila.
// ============================================

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SERVICE_PATHS } from '../../lib/servicePages';
import { useGuestSession } from '../../hooks/useGuestSession';
import { loadBest as loadPolpoBest } from '../../pages/services/polpo/engine';
import { loadBest as loadSaporiBest } from '../../pages/services/sapori/engine';
import { loadBest } from '../../pages/services/tarallo/game2048';
import { MemoryGameCard, PolpoGameCard, SaporiGameCard, TaralloGameCard } from './GameCards';
import { useHomeSession } from './HomeSessionHost';
import { useServiceDialogs } from './services/ServiceDialogsHost';
import { IconCircle, INK_TEXT, MUTED_TEXT, Section, SectionTitle } from './ui';

const Entertainment: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const { openService } = useServiceDialogs();
  const { openLogin } = useHomeSession();
  const { isLoggedIn } = useGuestSession();
  const [best] = useState(() => loadBest());
  const [saporiBest] = useState(() => loadSaporiBest());
  const [polpoBest] = useState(() => loadPolpoBest());
  const titleId = 'home-fun-title';

  return (
    <Section labelledBy={titleId} className={className} flushRight>
      <SectionTitle id={titleId} eyebrow={t('home.fun.eyebrow')} title={t('home.fun.title')} />
      <ul
        className="m-0 flex list-none snap-x snap-mandatory gap-2.5 overflow-x-auto p-0 pb-2 pr-4"
        style={{ scrollbarWidth: 'none', scrollPaddingLeft: 0 }}
      >
        <MemoryGameCard onClick={() => openService('game')} />
        <PolpoGameCard
          locked={!isLoggedIn}
          best={polpoBest}
          onClick={() => (isLoggedIn ? openService('polpo') : openLogin(SERVICE_PATHS.polpo))}
        />
        <SaporiGameCard
          locked={!isLoggedIn}
          best={saporiBest}
          onClick={() => (isLoggedIn ? openService('sapori') : openLogin(SERVICE_PATHS.sapori))}
        />
        <TaralloGameCard
          locked={!isLoggedIn}
          best={best}
          onClick={() => (isLoggedIn ? openService('tarallo') : openLogin(SERVICE_PATHS.tarallo))}
        />
        <li className="w-[min(40vw,150px)] flex-shrink-0 snap-start">
          <div className="flex h-full flex-col items-center justify-center gap-1.5 rounded-[22px] border-[1.5px] border-dashed border-nva-foot p-3 text-center dark:border-stone-700">
            <IconCircle>
              <Plus className="text-nva-gold-text dark:text-nva-gold-dark" />
            </IconCircle>
            <span className={cn('text-[13.5px] font-bold leading-snug', INK_TEXT)}>{t('home.fun.soonTitle')}</span>
            <span className={cn('text-[12px] leading-snug', MUTED_TEXT)}>{t('home.fun.soonDesc')}</span>
          </div>
        </li>
      </ul>
    </Section>
  );
};

export default Entertainment;
